import { Component } from "preact";
import PropTypes from "prop-types";
import { combineClasses, EO } from "@green24/npm-javascript-utils";
import Interpret from "../../components/shared/Localization/Interpret";
import withConfiguration from "../../components/shared/hoc/withConfiguration";
import DeveloperConfiguration from "./DeveloperConfiguration";
import { E_Keyboard_Type } from "../../models/constants/Enums_Keyboard";
import { configurationThunk } from "../../store/store";
import ButtonsConstructor from "../../components/shared/Input/Button/ButtonsConstructor";
import OptionsInput from "../../components/shared/Input/OptionsInput";
import { withLocalizationContext } from "../../components/components";
import { AppUtils } from "../../utils/AppUtils";
import MessageModal from "../../components/shared/Modal/MessageModal";
import Loading from "../../components/shared/Loading";
import { route } from "preact-router";
import { E_Route } from "../../models/constants/Enum_Routes";
import { DEFAULT_CONFIGURATION, E_Configuration_DisplayMode } from "../../models/Models_Configuration";
import ToggleInput from "../../components/shared/Input/ToggleInput";

class ConfigurationPage extends Component {
	constructor(props) {
		super();

		this.state = {
			configuration: props.configuration,
		}
	}

	_renderConfigurationSettings() {
		const {localizationContext, configurationStates} = this.props;
		const {configuration} = this.state;
		const onModify = (...props) => this._modifyConfiguration(...props);

		return <div>
			<h1 className={"separator"}><Interpret id={"settings.configuration"}/></h1>

			<div>
				<div className={"input-row"}>
					<label><h2>API</h2></label>

					<ButtonsConstructor buttons={[
						{
							className: "no-style keyboard-trigger",
							text: configuration.apiURL,
							textTranslated: true,
							action: () => {
								window.keyboard.open(
									E_Keyboard_Type.EMAIL,
									configuration.apiURL,
									undefined,
									value => onModify(value, `apiURL`),
									{
										maxLength: false,
									}
								)
							},
							style: AppUtils.isProduction ? {} : {marginLeft: "auto", marginRight: "-3px"}
						},
						AppUtils.isDev && {
							text: "settings.configuration.devAPI.set",
							action: () => onModify(DEFAULT_CONFIGURATION.devApiURL, `apiURL`),
						}
					]}/>
				</div>

				<OptionsInput
					options={localizationContext.internationalizer.getAllRegions().map(region => ({
						text: `locale.${region.toUpperCase()}`,
						value: region,
					}))}
					label={"settings.configuration.defaultLanguage"}
					value={configuration.defaultLanguage}
					onModify={(value) => onModify(value, `defaultLanguage`)}
				/>

				<div className={"input-row"}>
					<label><h2><Interpret id={"location.id"}/></h2></label>

					<ButtonsConstructor buttons={[
						{
							className: "no-style keyboard-trigger",
							text: `${configuration.locationID || ''}`,
							textTranslated: true,
							action: () => {
								window.keyboard.open(
									E_Keyboard_Type.NUMERIC,
									configuration.locationID,
									undefined,
									value => onModify(value, `locationID`),
								)
							}
						}
					]}/>
				</div>

				<div className={"input-row"}>
					<label><h2><Interpret id={"location.unit.id"}/></h2></label>

					<ButtonsConstructor buttons={[
						{
							className: "no-style keyboard-trigger",
							text: `${configuration.unitID || ''}`,
							textTranslated: true,
							action: () => {
								window.keyboard.open(
									E_Keyboard_Type.NUMERIC,
									configuration.unitID,
									undefined,
									value => onModify(value, `unitID`),
								)
							}
						}
					]}/>
				</div>

				<div className={"input-row"}>
					<label><h2><Interpret id={"frontendID"}/></h2></label>

					<ButtonsConstructor buttons={[
						{
							className: "no-style keyboard-trigger",
							text: `${configuration.frontendID || ''}`,
							textTranslated: true,
							action: () => {
								window.keyboard.open(
									E_Keyboard_Type.EMAIL,
									configuration.frontendID,
									undefined,
									value => onModify(value, `frontendID`),
									{
										maxLength: 255,
									}
								)
							}
						}
					]}/>
				</div>

				<OptionsInput
					options={Object.values(E_Configuration_DisplayMode).map(mode => ({
						text: `settings.configuration.displayMode.${mode}`,
						value: mode,
					}))}
					label={"settings.configuration.displayMode"}
					value={configuration.mode}
					onModify={value => onModify(value, `mode`)}
					isActive={(value, option) => option.value === value || (option.value === E_Configuration_DisplayMode.DEFAULT && value === null)}
				/>

				<ToggleInput
					label={"settings.configuration.allowDelivery"}
					value={configuration.allowDelivery}
					onModify={value => onModify(value, `allowDelivery`)}
				/>

				<ToggleInput
					style={{marginLeft: "2rem"}}
					label={"settings.configuration.allowDeliveryUserSelection"}
					value={configuration.allowDeliveryUserSelection}
					onModify={value => onModify(value, `allowDeliveryUserSelection`)}
					disabled={!configuration.allowDelivery}
				/>

				<ToggleInput
					style={{marginLeft: "2rem"}}
					label={"settings.configuration.allowDeliveryManualUserEntry"}
					value={configuration.allowDeliveryManualUserEntry}
					onModify={value => onModify(value, `allowDeliveryManualUserEntry`)}
					disabled={!configuration.allowDelivery}
				/>
			</div>

			<nav className={"action-buttons"}>
				<ButtonsConstructor
					buttons={[
						{
							className: "save",
							accent: "success",
							icon: "save",
							text: "save",
							action: () => this._saveConfiguration(),
						},
						{
							className: "clear-storage",
							accent: "error",
							icon: "eraser",
							text: "settings.configuration.clear.title",
							action: () => this._openClearStorageWarningModal(),
						}
					]}
					sharedProps={{
						disabled: configurationStates.fetching,
					}}
				/>
			</nav>
		</div>
	}

	_renderSettings() {
		const {configurationStates} = this.props;

		if(configurationStates.pending) {
			return <Loading block={true}/>;
		}

		return <>
			{
				AppUtils.isDev &&
				<DeveloperConfiguration/>
			}

			{this._renderConfigurationSettings()}
		</>
	}

	render({className, style}) {
		return (
			<section className={combineClasses("configuration-page", className)} style={style}>
				{this._renderSettings()}
			</section>
		);
	}

	_modifyConfiguration(value, path, type) {
		this.setState(state => {
			let newConfig = EO(state.configuration).clone();
			newConfig.modify(type, path, value);

			return {
				configuration: newConfig,
			}
		});
	}

	_saveConfiguration() {
		configurationThunk.saveConfiguration(this.state.configuration);
		route(E_Route.BASE);
	}

	_openClearStorageWarningModal() {
		window.modal.open(
			<MessageModal
				icon={"eraser"}
				title={"settings.configuration.clear.title"}
				message={"settings.configuration.clear.message"}
				actionButtons={() => [
					{
						className: "close",
						text: "no",
						action: () => window.modal.close(),
					},
					{
						className: "accent-fill",
						text: "yes",
						accent: "warning",
						action: () => {
							configurationThunk.clearConfiguration();
							window.modal.close();
						},
					}
				]}
			/>
		);
	}

	static get propTypes() {
		return {
			className: PropTypes.string,
			style: PropTypes.object,
			children: PropTypes.any,

			//withConfiguration
			configuration: PropTypes.object,
			configurationStates: PropTypes.object,

			//withLocalizationContext
			localizationContext: PropTypes.object,
		}
	}
}

export default withConfiguration(withLocalizationContext(ConfigurationPage));
